import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getScannedQRCodes } from '../services/qrService';
import { getDB } from '../utils/crypto';
import { getPendingSyncCount, addToSyncQueue, processSyncQueue } from '../utils/sync';
import { getUserGroups, getGroupProgress, recordGroupScan, submitGroupAnswer } from '../services/groupService';
import type { GroupProgress } from '../services/groupService';
import QRScanner from '../components/QRScanner';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import '../styles/groups.css';

// Interface for question/challenge item
interface ChallengeItem {
  number: string;
  question: string;
  scanned: boolean;
  answered: boolean;
  synced: boolean; // Whether the data is synced with the server
  markdownContent?: string;
  scannedBy?: string | null;
  answeredBy?: string | null;
}

// Sample questions (would normally come from a database or config file)
const challengeQuestions: { [key: string]: string } = {
  '01': 'Wie heißt das Maskottchen der TU Braunschweig?',
  '02': 'In welchem Jahr wurde die TU Braunschweig gegründet?',
  '03': 'Wie heißt das Hauptgebäude der TU Braunschweig?',
  '04': 'Wie viele Fakultäten hat die TU Braunschweig?',
  '05': 'Wie lautet das Motto der TU Braunschweig?',
  '06': 'Welches Tier ist im Logo der TU Braunschweig zu sehen?',
  '07': 'Wie heißt der berühmteste Mathematiker, der in Braunschweig studierte?',
  '08': 'Wie heißt der große Park neben der Universität?',
  '09': 'Welche Farbe hat das TU-Logo?',
  '10': 'Wie heißt die Universitätsbibliothek?',
  '11': 'Wie viele Studierende hat die TU Braunschweig ungefähr?',
  '12': 'Wie heißt das Studierendenparlament abgekürzt?',
  '13': 'Welches berühmte Fahrzeug wurde von TU-Professoren entwickelt?',
  '14': 'Wie heißt das Gebäude der Informatik?',
  '15': 'Wer ist der aktuelle Präsident der TU Braunschweig?',
  '16': 'Wie viele Mensen gibt es auf dem Campus?',
};

const CatalogView: React.FC = () => {
  const [challenges, setChallenges] = useState<ChallengeItem[]>([]);
  const [online, setOnline] = useState<boolean>(navigator.onLine);
  const [pendingSyncs, setPendingSyncs] = useState<number>(0);
  const [showScanner, setShowScanner] = useState<boolean>(false);
  const [selectedChallenge, setSelectedChallenge] = useState<string | null>(null);
  const [answer, setAnswer] = useState<string>('');
  const [scanSuccess, setScanSuccess] = useState<string | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);
  const [mainMarkdown, setMainMarkdown] = useState<string>('');
  const [unsyncedChanges, setUnsyncedChanges] = useState<boolean>(false);
  
  // Group-related state
  const [userGroups, setUserGroups] = useState<Array<{ id: string, name: string }>>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // This state will be used for displaying group progress
  // @ts-ignore - Will be implemented in a future update
  const [groupProgress, setGroupProgress] = useState<GroupProgress[]>([]);

  // Fetch markdown content for a specific challenge
  const fetchMarkdownContent = useCallback(async (qrNumber: string) => {
    try {
      const response = await fetch(`/questions/${qrNumber}.md`);
      
      if (response.ok) {
        const content = await response.text();
        
        // Update the challenges state with the fetched content
        setChallenges(prevChallenges => 
          prevChallenges.map(c => 
            c.number === qrNumber ? { ...c, markdownContent: content } : c
          )
        );
        
        return content;
      } else {
        console.error(`Failed to load markdown for question ${qrNumber}`);
        return null;
      }
    } catch (error) {
      console.error(`Error loading markdown content for question ${qrNumber}:`, error);
      return null;
    }
  }, []); // Remove challenges dependency

  // Update challenges with group progress
  const updateChallengesWithGroupProgress = useCallback((progress: GroupProgress[]) => {
    // Create a map for quick access to progress data by question number
    const progressMap = progress.reduce((map, item) => {
      const paddedNumber = item.qr_number.toString().padStart(2, '0');  // Changed to qr_number
      map[paddedNumber] = item;
      return map;
    }, {} as {[key: string]: GroupProgress});
    
    // Update challenges with group progress info using functional update
    setChallenges(prevChallenges => 
      prevChallenges.map(challenge => {
        const progressItem = progressMap[challenge.number];
        
        // Update scanned and answered status based on group progress
        if (progressItem) {
          return {
            ...challenge,
            scanned: challenge.scanned || !!progressItem.scanned_at, // Keep local scanned or update from server
            answered: challenge.answered || !!progressItem.answered_at, // Keep local answered or update from server
            synced: true, // Data from server is synced
            // Add additional info for display
            scannedBy: progressItem.scanned_by_username,
            answeredBy: progressItem.answered_by_username,
          };
        }
        
        return challenge;
      })
    );
  }, []); // Remove challenges dependency

  // Load main markdown content
  useEffect(() => {
    const loadMainMarkdown = async () => {
      try {
        const response = await fetch('/questions/main.md');
        if (response.ok) {
          const content = await response.text();
          setMainMarkdown(content);
        } else {
          console.error('Failed to load main markdown content');
        }
      } catch (error) {
        console.error('Error loading main markdown:', error);
      }
    };
    
    loadMainMarkdown();
  }, []);

  // Initialize challenges
  useEffect(() => {
    const initChallenges = async () => {
      // Create an array of challenge items
      const items: ChallengeItem[] = [];
      
      for (let i = 1; i <= 16; i++) {
        const number = i.toString().padStart(2, '0');
        const isSpecialChallenge = i === 15 || i === 16; // No QR/answer needed
        
        items.push({
          number,
          question: challengeQuestions[number] || `Frage ${number}`,
          scanned: isSpecialChallenge, // Automatically marked as scanned
          answered: isSpecialChallenge, // Automatically marked as answered
          synced: isSpecialChallenge // Automatically marked as synced
        });
      }
      
      // Get scanned QR codes from IndexedDB
      const scannedCodes = await getScannedQRCodes();
      
      // Get answers from IndexedDB
      const db = await getDB();
      const answers = await db.getAll('user_answers');
      
      // Update challenges with scan and answer status (skip 15 and 16)
      const updatedItems = items.map(item => {
        const isSpecialChallenge = item.number === '15' || item.number === '16';
        
        if (isSpecialChallenge) {
          // Special challenges are always complete
          return item;
        }
        
        const scanned = scannedCodes.some(code => code.qrNumber === item.number);
        const answered = answers.some(ans => ans.qrNumber === item.number);
        
        return {
          ...item,
          scanned,
          answered,
          synced: false // Local data is not synced initially
        };
      });
      
      setChallenges(updatedItems);
      
      // Load markdown for already scanned challenges (skip 15 and 16)
      for (const item of updatedItems.filter(c => c.scanned && c.number !== '15' && c.number !== '16')) {
        fetchMarkdownContent(item.number);
      }
      
      // Load user groups and group progress
      try {
        const groups = await getUserGroups();
        setUserGroups(groups);
        
        if (groups.length > 0) {
          // Load group progress for the first (and only) group
          const progress = await getGroupProgress(groups[0].id);
          setGroupProgress(progress);
        }
      } catch (error) {
        console.error('Error loading group data:', error);
      }
    };
    
    initChallenges();
    // fetchMarkdownContent is stable (empty deps array)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount
  
  // Listen for group changes - reload progress when user groups change
  useEffect(() => {
    if (userGroups.length === 0) {
      // Clear any pending answer when user leaves group
      setAnswer('');
      return;
    }
    
    const loadGroupProgress = async () => {
      try {
        const progress = await getGroupProgress(userGroups[0].id);
        setGroupProgress(progress);
        updateChallengesWithGroupProgress(progress);
      } catch (error) {
        console.error('Error loading group progress:', error);
      }
    };
    
    loadGroupProgress();
    // updateChallengesWithGroupProgress is stable (empty deps array)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGroups]); // Re-run when userGroups changes

  // Setup network status monitoring
  useEffect(() => {
    // Check for pending syncs initially
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    // This function will be used in a future update to display sync status
    // @ts-ignore - Will be implemented in a future update
    const checkPendingSyncs = async () => {
      const count = await getPendingSyncCount();
      setPendingSyncs(count);
      setUnsyncedChanges(count > 0);
    };
    
    // Update online status and check for unsynced changes
    const handleOnlineStatusChange = async () => {
      setOnline(navigator.onLine);
      
      if (navigator.onLine) {
        // Process sync queue when coming back online
        try {
          await processSyncQueue();
        } catch (error) {
          console.error('Error processing sync queue:', error);
        }
        
        // Check for pending syncs after sync attempt
        const count = await getPendingSyncCount();
        setPendingSyncs(count);
        setUnsyncedChanges(count > 0);
        
        // Reload challenges to reflect synced status
        const db = await getDB();
        const scannedCodes = await getScannedQRCodes();
        const answers = await db.getAll('user_answers');
        
        // Get sync queue to check what's still pending
        const syncQueue = await db.getAll('sync_queue');
        const pendingItems = syncQueue.filter(item => !item.synced);
        
        // Update challenges with current status
        setChallenges(prevChallenges => prevChallenges.map(item => {
          const scanned = scannedCodes.some(code => code.qrNumber === item.number);
          const answered = answers.some(ans => ans.qr_number?.toString().padStart(2, '0') === item.number || ans.qrNumber === item.number);
          
          // Check if this item has pending sync operations
          const qrNum = parseInt(item.number);
          const hasPendingScan = pendingItems.some(
            syncItem => syncItem.table === 'qr_scans' && syncItem.data.qr_number === qrNum
          );
          const hasPendingAnswer = pendingItems.some(
            syncItem => syncItem.table === 'user_answers' && syncItem.data.qr_number === qrNum
          );
          
          const synced = (scanned && !hasPendingScan) || (answered && !hasPendingAnswer);
          
          return {
            ...item,
            scanned,
            answered,
            synced
          };
        }));
      }
    };
    
    // Use both the window events and regular pings to check connection status
    const checkNetworkStatus = () => {
      // Use navigator.onLine as initial value
      const isOnline = navigator.onLine;
      
      // Update state immediately based on navigator.onLine
      setOnline(isOnline);
      
      // If we think we're online, verify with a small fetch request
      if (isOnline) {
        // Create a timestamp to prevent caching
        const timestamp = new Date().getTime();
        
        // Try to fetch a small resource
        fetch(`/favicon.ico?_=${timestamp}`, { 
          method: 'HEAD',
          cache: 'no-store',
          mode: 'no-cors'
        })
        .then(() => {
          // Connection is good
          setOnline(true);
          // Check for pending syncs when online
          getPendingSyncCount().then(count => {
            setPendingSyncs(count);
            setUnsyncedChanges(count > 0);
          });
        })
        .catch(() => {
          // Connection failed
          setOnline(false);
        });
      }
    };
    
    // Use standard events
    window.addEventListener('online', handleOnlineStatusChange);
    window.addEventListener('offline', handleOnlineStatusChange);
    
    // Also use more aggressive checking
    const networkCheckInterval = setInterval(checkNetworkStatus, 5000); // Check every 5 seconds
    checkNetworkStatus(); // Initial check
    
    // Update pending sync count and unsynced status
    const updateSyncStatus = async () => {
      const count = await getPendingSyncCount();
      setPendingSyncs(count);
      setUnsyncedChanges(count > 0);
    };
    
    updateSyncStatus();
    const syncInterval = setInterval(updateSyncStatus, 30000); // Check every 30 seconds
    
    return () => {
      window.removeEventListener('online', handleOnlineStatusChange);
      window.removeEventListener('offline', handleOnlineStatusChange);
      clearInterval(syncInterval);
      clearInterval(networkCheckInterval);
    };
  }, []);

  // Handle QR scan success
  const handleScanSuccess = async (qrNumber: string) => {
    // Check if user is in a group before allowing scan
    if (userGroups.length === 0) {
      setScanError('Du musst einer Gruppe beitreten, bevor du QR-Codes scannen kannst!');
      setShowScanner(false);
      return;
    }
    
    setScanSuccess(`QR-Code ${qrNumber} erfolgreich gescannt!`);
    
    try {
      // Save scan to IndexedDB
      const db = await getDB();
      const timestamp = new Date().toISOString();
      
      // Create scan object
      const scanData = {
        qrNumber,
        timestamp
      };
      
      // Record the scan in IndexedDB
      await db.add('qr_scans', scanData);
      
      // Add to sync queue for server synchronization
      await addToSyncQueue('qr_scans', 'INSERT', scanData);
      
      // Update challenges
      const updatedChallenges = challenges.map(item => {
        if (item.number === qrNumber) {
          return { ...item, scanned: true, synced: false };
        }
        return item;
      });
      
      setChallenges(updatedChallenges);
      setShowScanner(false);
      setSelectedChallenge(qrNumber);
      
      // Try to load markdown content for this challenge
      const content = await fetchMarkdownContent(qrNumber);
      if (content) {
        updatedChallenges.find(c => c.number === qrNumber)!.markdownContent = content;
        setChallenges([...updatedChallenges]);
      }
      
      // If user is in a group, update group progress
      if (userGroups.length > 0 && userGroups[0].id) {
        try {
          const { success, error } = await recordGroupScan(userGroups[0].id, qrNumber);
          if (!success) {
            console.error('Failed to update group progress:', error);
          } else {
            // Refresh group progress
            const progress = await getGroupProgress(userGroups[0].id);
            setGroupProgress(progress);
            updateChallengesWithGroupProgress(progress);
          }
        } catch (groupError) {
          console.error('Error updating group progress:', groupError);
        }
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setScanSuccess(null);
      }, 3000);
    } catch (error) {
      console.error('Error recording scan:', error);
      setScanError('Fehler beim Speichern des Scans.');
      setTimeout(() => setScanError(null), 3000);
    }
  };

  // Handle QR scan error
  const handleScanError = (error: string) => {
    setScanError(error);
    
    // Clear error message after 3 seconds
    setTimeout(() => {
      setScanError(null);
    }, 3000);
  };

  const [answerFeedback, setAnswerFeedback] = useState<{type: 'success' | 'error', message: string} | null>(null);
  
  // Submit answer for a challenge
  const handleAnswerSubmit = async () => {
    if (!selectedChallenge || !answer.trim()) {
      setAnswerFeedback({type: 'error', message: 'Bitte gib eine Antwort ein.'});
      return;
    }
    
    // Check if user is in a group before allowing answer submission
    if (userGroups.length === 0) {
      setAnswerFeedback({type: 'error', message: 'Du musst einer Gruppe beitreten, bevor du Antworten einreichen kannst!'});
      return;
    }
    
    try {
      // Basic validation
      if (answer.trim().length < 2) {
        setAnswerFeedback({type: 'error', message: 'Die Antwort ist zu kurz.'});
        return;
      }
      
      // Save answer to IndexedDB
      const db = await getDB();
      const timestamp = new Date().toISOString();
      
      // Get user info from IndexedDB
      const userInfo = await db.get('user_data', 'user_info');
      const userId = userInfo?.user_id;
      
      if (!userId) {
        setAnswerFeedback({type: 'error', message: 'Benutzer-ID nicht gefunden. Bitte neu anmelden.'});
        return;
      }
      
      // Create answer object with correct database schema
      const answerData = {
        user_id: userId,
        qr_number: parseInt(selectedChallenge),
        answer_text: answer.trim(),
        submitted_at_client: timestamp
      };
      
      // Add to user_answers store
      await db.add('user_answers', answerData);
      
      // Add to sync queue for server synchronization when online
      await addToSyncQueue('user_answers', 'INSERT', answerData);
      
      // If online, immediately process the sync queue
      if (navigator.onLine) {
        processSyncQueue().catch(console.error);
      }
      
      // Update challenges
      const updatedChallenges = challenges.map(item => {
        if (item.number === selectedChallenge) {
          return { ...item, answered: true, synced: false };
        }
        return item;
      });
      
      setChallenges(updatedChallenges);
      
      // If user is in a group, update group progress
      if (userGroups.length > 0 && userGroups[0].id) {
        try {
          const { success, error } = await submitGroupAnswer(
            userGroups[0].id, 
            selectedChallenge, 
            answer.trim()
          );
          
          if (!success) {
            console.error('Failed to update group answer:', error);
          } else {
            // Refresh group progress
            const progress = await getGroupProgress(userGroups[0].id);
            setGroupProgress(progress);
            updateChallengesWithGroupProgress(progress);
          }
        } catch (groupError) {
          console.error('Error updating group answer:', groupError);
        }
      }
      
      // Show success message
      setAnswerFeedback({type: 'success', message: 'Antwort gespeichert!'});
      
      // Clear and redirect after delay
      setTimeout(() => {
        setAnswer('');
        setSelectedChallenge(null);
        setAnswerFeedback(null);
      }, 1500);
    } catch (error) {
      console.error('Error saving answer:', error);
      setAnswerFeedback({type: 'error', message: 'Fehler beim Speichern der Antwort.'});
      setTimeout(() => setAnswerFeedback(null), 3000);
    }
  };

  // Get status color for a challenge
  const getChallengeColor = (challenge: ChallengeItem) => {
    const classes = [];
    
    // All 8 possible states with unique colors:
    // State 1: Not scanned, not answered -> Gray
    // State 2: Scanned only, not synced -> Yellow
    // State 3: Scanned only, synced -> Light Yellow
    // State 4: Answered only, not synced -> Purple
    // State 5: Answered only, synced -> Light Purple
    // State 6: Both scanned and answered, not synced -> Orange
    // State 7: Both scanned and answered, synced -> Light Orange
    // State 8: Both scanned and answered, both synced -> Green (complete)
    
    if (!challenge.scanned && !challenge.answered) {
      // State 1: Nothing done yet
      classes.push('gray');
    } else if (!challenge.scanned && challenge.answered && !challenge.synced) {
      // State 4: Answered only, not synced
      classes.push('purple');
    } else if (!challenge.scanned && challenge.answered && challenge.synced) {
      // State 5: Answered only, synced
      classes.push('light-purple');
    } else if (challenge.scanned && !challenge.answered && !challenge.synced) {
      // State 2: Scanned only, not synced
      classes.push('yellow');
    } else if (challenge.scanned && !challenge.answered && challenge.synced) {
      // State 3: Scanned only, synced
      classes.push('light-yellow');
    } else if (challenge.scanned && challenge.answered && !challenge.synced) {
      // State 6: Both done, not synced
      classes.push('orange');
    } else if (challenge.scanned && challenge.answered && challenge.synced) {
      // State 7 & 8: Both done, synced - COMPLETE
      classes.push('green');
    }
    
    if (selectedChallenge === challenge.number) classes.push('selected');
    
    return classes.join(' ');
  };

  return (
    <div className="catalog-view">
      <div className="status-bar">
        <div className={`online-status ${online ? (unsyncedChanges ? 'unsynced' : 'online') : 'offline'}`}>
          {online 
            ? (unsyncedChanges ? 'Unsynced' : 'Online') 
            : 'Offline'
          }
        </div>
        <Link to="/groups" className="groups-nav-button" title="Gruppen verwalten">
          👥 Gruppen
        </Link>
        {pendingSyncs > 0 && (
          <div className="pending-syncs-badge">
            ⏳ {pendingSyncs}
          </div>
        )}
      </div>
      
      {scanSuccess && (
        <div className="success-message">{scanSuccess}</div>
      )}
      
      {scanError && (
        <div className="error-message">{scanError}</div>
      )}
      
      {/* Current group display */}
      {userGroups.length > 0 ? (
        <div className="current-group-display">
          Gruppe: <strong>{userGroups[0].name}</strong>
        </div>
      ) : (
        <div className="warning-message" style={{
          backgroundColor: '#fff3cd',
          color: '#856404',
          padding: '1rem',
          borderRadius: '8px',
          margin: '1rem 0',
          border: '2px solid #ffc107',
          textAlign: 'center',
          fontSize: '1rem',
          fontWeight: 'bold'
        }}>
          ⚠️ Du musst einer Gruppe beitreten, um QR-Codes zu scannen und Antworten einzureichen!
          <br />
          <span style={{ fontSize: '0.9rem', fontWeight: 'normal' }}>
            Erstelle oder tritt einer Gruppe bei, um teilzunehmen.
          </span>
        </div>
      )}
      
      {showScanner ? (
        <div className="scanner-container">
          <QRScanner 
            onSuccess={handleScanSuccess} 
            onError={handleScanError}
          />
          <button 
            className="cancel-button"
            onClick={() => setShowScanner(false)}
          >
            Abbrechen
          </button>
        </div>
      ) : selectedChallenge ? (
        <div className="answer-form">
          <h2>Frage {selectedChallenge}</h2>
          
          {selectedChallenge && challenges.find(c => c.number === selectedChallenge)?.markdownContent ? (
            <div className="markdown-content">
              <ReactMarkdown 
                rehypePlugins={[rehypeSanitize]} 
                remarkPlugins={[remarkGfm]}
              >
                {challenges.find(c => c.number === selectedChallenge)?.markdownContent || ''}
              </ReactMarkdown>
            </div>
          ) : (
            <p>Frage wird geladen...</p>
          )}
          
          {answerFeedback && (
            <div className={`feedback-message ${answerFeedback.type}`}>
              {answerFeedback.message}
            </div>
          )}
          
          {/* Special challenges 15 and 16 don't require answers */}
          {selectedChallenge !== '15' && selectedChallenge !== '16' ? (
            <>
              {userGroups.length === 0 && (
                <div className="warning-message" style={{
                  backgroundColor: '#fff3cd',
                  color: '#856404',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  marginBottom: '1rem',
                  border: '1px solid #ffc107',
                  fontSize: '0.9rem'
                }}>
                  ⚠️ Du musst einer Gruppe beitreten, um Antworten einzureichen.
                </div>
              )}
              <textarea 
                value={answer} 
                onChange={(e) => {
                  // Only allow answer changes if user is in a group
                  if (userGroups.length > 0) {
                    setAnswer(e.target.value);
                  }
                }}
                placeholder={userGroups.length === 0 ? "Tritt zuerst einer Gruppe bei..." : "Deine Antwort hier eingeben..."}
                rows={4}
                disabled={userGroups.length === 0}
                style={userGroups.length === 0 ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
              ></textarea>
              
              <div className="button-group">
                <button 
                  className="submit-button" 
                  onClick={handleAnswerSubmit}
                  disabled={!answer.trim() || userGroups.length === 0}
                  title={userGroups.length === 0 ? "Du musst einer Gruppe beitreten" : ""}
                >
                  Antwort einreichen
                </button>
                <button 
                  className="cancel-button"
                  onClick={() => setSelectedChallenge(null)}
                >
                  Abbrechen
                </button>
              </div>
            </>
          ) : (
            <div className="button-group">
              <button 
                className="cancel-button"
                onClick={() => setSelectedChallenge(null)}
              >
                Schließen
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="header-section">
            <img 
              src="/images/FGLogoEnteSchriftAus.png" 
              alt="Fachgruppe Logo" 
              className="fg-logo"
            />
            <h1 className="pixel-title">Rätseljagd TU Braunschweig</h1>
          </div>
          
          <div className="challenge-grid">
            {challenges.slice(0, 8).map(challenge => (
              <div 
                key={challenge.number}
                className={`challenge-box ${getChallengeColor(challenge)}`}
                onClick={async () => {
                  // If we don't already have the markdown content, fetch it
                  if (!challenge.markdownContent) {
                    await fetchMarkdownContent(challenge.number);
                  }
                  
                  // Toggle selected state - deselect if already selected
                  setSelectedChallenge(prevSelected => 
                    prevSelected === challenge.number ? null : challenge.number
                  );
                }}
              >
                <div className="challenge-number">{challenge.number}</div>
                {challenge.number === '15' || challenge.number === '16' ? (
                  <div className="challenge-special-badge" title="Keine QR-Code-Scan nötig">
                    ⭐
                  </div>
                ) : userGroups.length > 0 && challenge.scanned && (
                  <div className="challenge-group-info">
                    {challenge.scannedBy && (
                      <span className="scanned-by" title={`Gescannt von ${challenge.scannedBy}`}>
                        👁 {challenge.scannedBy}
                      </span>
                    )}
                    {challenge.answered && challenge.answeredBy && (
                      <span className="answered-by" title={`Beantwortet von ${challenge.answeredBy}`}>
                        ✓ {challenge.answeredBy}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="challenge-grid">
            {challenges.slice(8, 16).map(challenge => (
              <div 
                key={challenge.number}
                className={`challenge-box ${getChallengeColor(challenge)}`}
                onClick={async () => {
                  // If we don't already have the markdown content, fetch it
                  if (!challenge.markdownContent) {
                    await fetchMarkdownContent(challenge.number);
                  }
                  
                  // Toggle selected state - deselect if already selected
                  setSelectedChallenge(prevSelected => 
                    prevSelected === challenge.number ? null : challenge.number
                  );
                }}
              >
                <div className="challenge-number">{challenge.number}</div>
                {challenge.number === '15' || challenge.number === '16' ? (
                  <div className="challenge-special-badge" title="Keine QR-Code-Scan nötig">
                    ⭐
                  </div>
                ) : userGroups.length > 0 && challenge.scanned && (
                  <div className="challenge-group-info">
                    {challenge.scannedBy && (
                      <span className="scanned-by" title={`Gescannt von ${challenge.scannedBy}`}>
                        👁 {challenge.scannedBy}
                      </span>
                    )}
                    {challenge.answered && challenge.answeredBy && (
                      <span className="answered-by" title={`Beantwortet von ${challenge.answeredBy}`}>
                        ✓ {challenge.answeredBy}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="main-content">
            {selectedChallenge && challenges.find(c => c.number === selectedChallenge)?.markdownContent ? (
              <div className="markdown-content question-markdown">
                <h3>Frage {selectedChallenge}</h3>
                <ReactMarkdown 
                  rehypePlugins={[rehypeSanitize]} 
                  remarkPlugins={[remarkGfm]}
                >
                  {challenges.find(c => c.number === selectedChallenge)?.markdownContent || ''}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="markdown-content main-markdown">
                <ReactMarkdown 
                  rehypePlugins={[rehypeSanitize]} 
                  remarkPlugins={[remarkGfm]}
                >
                  {mainMarkdown}
                </ReactMarkdown>
              </div>
            )}
          </div>
          
          {/* Legend for box colors */}
          <div className="legend-section">
            <h3>Legende:</h3>
            <div className="legend-items">
              <div className="legend-item">
                <div className="legend-box gray"></div>
                <span>Nicht gescannt & nicht beantwortet</span>
              </div>
              <div className="legend-item">
                <div className="legend-box yellow"></div>
                <span>Nur gescannt (nicht synchronisiert)</span>
              </div>
              <div className="legend-item">
                <div className="legend-box light-yellow"></div>
                <span>Nur gescannt (synchronisiert)</span>
              </div>
              <div className="legend-item">
                <div className="legend-box purple"></div>
                <span>Nur beantwortet (nicht synchronisiert)</span>
              </div>
              <div className="legend-item">
                <div className="legend-box light-purple"></div>
                <span>Nur beantwortet (synchronisiert)</span>
              </div>
              <div className="legend-item">
                <div className="legend-box orange"></div>
                <span>Beides (nicht synchronisiert)</span>
              </div>
              <div className="legend-item">
                <div className="legend-box light-orange"></div>
                <span>Beides (teilweise synchronisiert)</span>
              </div>
              <div className="legend-item">
                <div className="legend-box green"></div>
                <span>Beides (vollständig synchronisiert) ✓</span>
              </div>
            </div>
          </div>
          
          {/* Footer with legal links */}
          <footer className="app-footer">
            <Link to="/impressum">Impressum</Link>
            <span className="footer-separator">|</span>
            <Link to="/datenschutz">Datenschutzerklärung</Link>
          </footer>
          
          {/* Floating camera button */}
          <button 
            className={`floating-camera-button ${userGroups.length === 0 ? 'disabled' : ''}`}
            onClick={() => {
              if (userGroups.length === 0) {
                setScanError('Du musst einer Gruppe beitreten, bevor du QR-Codes scannen kannst!');
                // Auto-clear the error after 5 seconds
                setTimeout(() => setScanError(null), 5000);
              } else {
                setShowScanner(true);
              }
            }}
            title={userGroups.length === 0 ? "Du musst einer Gruppe beitreten" : "QR-Code scannen"}
          >
            📷
          </button>
        </>
      )}
    </div>
  );
};

export default CatalogView;